/**
 * Per-row binding fingerprints for {@link LayoutList} preview rows.
 *
 * Collects binding expressions from row templates (layout nodes + behavior graphs),
 * resolves them against row-scoped {@link InjectedData}, and compares snapshots so
 * only rows whose displayed values changed are re-rendered.
 */

import type {
  ActionNode,
  BehaviorConnection,
  BehaviorNode as BlueprintGraphNode,
  BindingExpression,
  LayoutListActionNode,
} from "../blueprint/types.js";
import { ACTION_SET_KEY_VALUE_TYPE } from "../blueprint/types.js";
import { resolveBindingExpression } from "../blueprint/method.js";
import { normalizeListRowBindingPath } from "./resolution.js";
import { BehaviorRuntime } from "../blueprint/runtime/BehaviorRuntime.js";
import {
  ensureBindingExpression,
  stableSerialize,
} from "../blueprint/runtime/helpers.js";
import { SceneFunctionGraphRegistry } from "../blueprint/runtime/SceneFunctionGraphRegistry.js";
import { SceneVariableGraphRegistry } from "../blueprint/runtime/SceneVariableGraphRegistry.js";
import type { ExecutionContext } from "../blueprint/runtime/types.js";
import type {
  ContainerNode,
  DataSourceId,
  InjectedData,
  LayoutNode,
  SceneBehaviorCatalog,
} from "./types.js";
import { getDataBindingSource } from "./types.js";
import { DATA_SOURCE_PATHS } from "./data-source-schema.js";
import type { InjectableDataSourceId } from "./resolution.js";
import {
  injectedValuesEqual,
  shallowEqualRecords,
} from "./injected-data-stabilizer.js";
import { isDecimalLike } from "./resolution.js";

export interface RowBindingFingerprint {
  /** Resolved values for every collected binding expression (stable order). */
  values: unknown[];
  /** Matched row variant name, or `null` for the default row template. */
  matchedVariantName: string | null;
}

type BehaviorGraphPayload = {
  nodes: BlueprintGraphNode[];
  connections: BehaviorConnection[];
};

const STYLE_ACTION_KEYS = new Set(Object.keys(ACTION_SET_KEY_VALUE_TYPE));

/** Runtime row scope keys - changes are reflected via `layoutListRowItem` refs, not fingerprints. */
const ROW_TEMPLATE_CONTEXT_SOURCES = new Set<string>([
  "currentNode",
  "rowIndex",
  "layoutListRowItem",
  "sortAIndex",
  "sortBIndex",
  "listTransitionSignal",
  "iterationItem",
  "iterationIndex",
  "iterationItemA",
  "iterationItemB",
  "layoutListComparator",
]);

const INJECTABLE_DATA_SOURCE_IDS = new Set<string>(
  (Object.keys(DATA_SOURCE_PATHS) as DataSourceId[]).filter(
    (source) => !ROW_TEMPLATE_CONTEXT_SOURCES.has(source),
  ),
);

const rootPathFromBindingPath = (path: string): string | undefined => {
  const root = path.split(".")[0]?.trim();
  return root || undefined;
};

const isRowTemplateContextSource = (source: string): boolean =>
  ROW_TEMPLATE_CONTEXT_SOURCES.has(source);

/**
 * Returns whether a host-injectable source changed between two payloads.
 *
 * Uses reference equality on stabilized {@link InjectedData} fields.
 */
export function hasInjectableSourcesChanged(
  previous: InjectedData,
  next: InjectedData,
  sources: ReadonlySet<InjectableDataSourceId>,
): boolean {
  if (sources.size === 0) return false;
  for (const source of sources) {
    const key = source as keyof InjectedData;
    if (!Object.is(previous[key], next[key])) {
      return true;
    }
  }
  return false;
}

const markInjectableSource = (
  source: string | undefined,
  sink: Set<InjectableDataSourceId>,
  listSourceKey?: string,
): void => {
  if (!source || isRowTemplateContextSource(source)) return;
  if (listSourceKey && source === listSourceKey) return;
  if (!INJECTABLE_DATA_SOURCE_IDS.has(source)) return;
  sink.add(source as InjectableDataSourceId);
};

const collectSourcesFromBindingExpression = (
  expression: BindingExpression,
  sink: Set<InjectableDataSourceId>,
  listSourceKey?: string,
): void => {
  switch (expression.kind) {
    case "binding":
      markInjectableSource(
        rootPathFromBindingPath(expression.path),
        sink,
        listSourceKey,
      );
      return;
    case "literal":
      return;
    case "unary":
      collectSourcesFromBindingExpression(
        expression.input,
        sink,
        listSourceKey,
      );
      return;
    case "binary":
      collectSourcesFromBindingExpression(
        expression.input1,
        sink,
        listSourceKey,
      );
      collectSourcesFromBindingExpression(
        expression.input2,
        sink,
        listSourceKey,
      );
      return;
    case "ternary":
      collectSourcesFromBindingExpression(
        expression.input1,
        sink,
        listSourceKey,
      );
      collectSourcesFromBindingExpression(
        expression.input2,
        sink,
        listSourceKey,
      );
      collectSourcesFromBindingExpression(
        expression.input3,
        sink,
        listSourceKey,
      );
      return;
    default:
      return;
  }
};

const collectSourcesFromBehaviorGraph = (
  graph: BehaviorGraphPayload | undefined,
  catalog: SceneBehaviorCatalog | undefined,
  sink: Set<InjectableDataSourceId>,
  listSourceKey?: string,
): void => {
  if (!graph?.nodes?.length) return;

  for (const node of graph.nodes) {
    const objectSource = (node as { objectSource?: unknown }).objectSource;
    if (typeof objectSource === "string") {
      markInjectableSource(objectSource, sink, listSourceKey);
    }

    const variableId = (node as { variableId?: unknown }).variableId;
    const valueType = (node as { valueType?: unknown }).valueType;
    if (
      valueType === "variableRef" &&
      typeof variableId === "string" &&
      catalog?.variables?.length
    ) {
      const catalogEntry = catalog.variables.find(
        (entry) => entry.id === variableId,
      );
      if (catalogEntry?.graph) {
        collectSourcesFromBehaviorGraph(
          catalogEntry.graph,
          catalog,
          sink,
          listSourceKey,
        );
      }
    }
  }
};

const visitLayoutNodeForUsedSources = (
  node: LayoutNode,
  catalog: SceneBehaviorCatalog | undefined,
  sink: Set<InjectableDataSourceId>,
  listSourceKey?: string,
): void => {
  markInjectableSource(
    getDataBindingSource(node.dataBinding),
    sink,
    listSourceKey,
  );
  collectSourcesFromBehaviorGraph(node.behavior, catalog, sink, listSourceKey);

  if (node.kind === "container") {
    for (const child of node.children) {
      visitLayoutNodeForUsedSources(child, catalog, sink, listSourceKey);
    }
  }
};

/**
 * Injectable sources referenced by row templates (excluding list item scope and
 * the list's own `dataBinding` root). When none of these change between ticks,
 * row fingerprints can be reused and only `layoutListRowItem` ref changes drive re-renders.
 *
 * @param rowTemplate - Default row template.
 * @param rowVariants - Optional variant templates.
 * @param catalog - Scene catalog (for `variableRef` bodies).
 * @param listSourceKey - List binding root (e.g. `athletes`) excluded from externals.
 * @param extraExpressions - Extra expressions (e.g. layout-list variant conditions).
 */
export function collectRowTemplateUsedExternalSources(
  rowTemplate: ContainerNode,
  rowVariants: Array<{ name: string; template: ContainerNode }> | undefined,
  catalog: SceneBehaviorCatalog | undefined,
  listSourceKey?: string,
  extraExpressions: readonly BindingExpression[] = [],
): ReadonlySet<InjectableDataSourceId> {
  const sources = new Set<InjectableDataSourceId>();
  visitLayoutNodeForUsedSources(rowTemplate, catalog, sources, listSourceKey);
  for (const variant of rowVariants ?? []) {
    if (variant.template) {
      visitLayoutNodeForUsedSources(
        variant.template,
        catalog,
        sources,
        listSourceKey,
      );
    }
  }
  for (const expression of extraExpressions) {
    collectSourcesFromBindingExpression(expression, sources, listSourceKey);
  }
  return sources;
}

/**
 * Builds an execution context used only to capture binding AST from behavior graphs.
 */
export function createBindingCaptureContext(
  catalog?: SceneBehaviorCatalog,
): ExecutionContext {
  const variableRegistry = new SceneVariableGraphRegistry();
  if (catalog?.variables?.length) {
    variableRegistry.rebuild(catalog.variables);
  }
  const functionRegistry = new SceneFunctionGraphRegistry();
  if (catalog?.functions?.length) {
    functionRegistry.rebuild(catalog.functions);
  }
  const bootstrap = BehaviorRuntime.build({ nodes: [], connections: [] });
  return bootstrap.createContext(
    0,
    {} as InjectedData,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    functionRegistry.getEntries().size > 0
      ? functionRegistry.getEntries()
      : undefined,
    variableRegistry.getEntries().size > 0
      ? variableRegistry.getEntries()
      : undefined,
  );
}

const capturePortBinding = (
  runtime: BehaviorRuntime,
  nodeId: string,
  portId: string,
  context: ExecutionContext,
): BindingExpression | undefined => {
  const behaviorNode = runtime.nodes.get(nodeId);
  if (!behaviorNode) return undefined;
  const visiting = new Set<string>();
  const raw = behaviorNode.getInput(portId, visiting, context, true);
  return ensureBindingExpression(raw);
};

const collectFromBehaviorGraph = (
  graph: BehaviorGraphPayload | undefined,
  context: ExecutionContext,
  sink: BindingExpression[],
): void => {
  if (!graph?.nodes?.length) return;
  const runtime = BehaviorRuntime.build(graph);

  for (const node of graph.nodes) {
    if (node.type !== "action") continue;
    const action = node as ActionNode | LayoutListActionNode;

    if (action.kind === "set") {
      const key = action.key;
      if (key === "setBinding") {
        const expression = capturePortBinding(
          runtime,
          node.id,
          "value",
          context,
        );
        if (expression) sink.push(expression);
        continue;
      }
      if (
        key === "text" ||
        key === "location" ||
        key === "transform" ||
        STYLE_ACTION_KEYS.has(key)
      ) {
        const expression = capturePortBinding(
          runtime,
          node.id,
          "value",
          context,
        );
        if (expression) sink.push(expression);
      }
      if (key !== "location" && key !== "transform") {
        const targetExpression = capturePortBinding(
          runtime,
          node.id,
          "target",
          context,
        );
        if (targetExpression) sink.push(targetExpression);
      }
      continue;
    }

    if (action.kind === "createRowVariant") {
      const expression = capturePortBinding(
        runtime,
        node.id,
        "condition",
        context,
      );
      if (expression) sink.push(expression);
    }
  }
};

const visitLayoutNodeForBindings = (
  node: LayoutNode,
  captureContext: ExecutionContext,
  sink: BindingExpression[],
): void => {
  if (node.dataBinding?.path) {
    sink.push({
      kind: "binding",
      path: normalizeListRowBindingPath(node.dataBinding.path),
    });
  }

  collectFromBehaviorGraph(node.behavior, captureContext, sink);

  if (node.kind === "container") {
    for (const child of node.children) {
      visitLayoutNodeForBindings(child, captureContext, sink);
    }
  }
};

const dedupeBindingExpressions = (
  expressions: BindingExpression[],
): BindingExpression[] => {
  const seen = new Set<string>();
  const unique: BindingExpression[] = [];
  for (const expression of expressions) {
    const key = stableSerialize(expression);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(expression);
  }
  return unique;
};

/**
 * Collects all binding expressions referenced by a row template subtree.
 *
 * @param template - Row template container (or variant template).
 * @param catalog - Scene catalog for `variableRef` / `call` capture.
 */
export function collectRowTemplateBindingExpressions(
  template: ContainerNode,
  catalog?: SceneBehaviorCatalog,
): BindingExpression[] {
  const captureContext = createBindingCaptureContext(catalog);
  const expressions: BindingExpression[] = [];
  visitLayoutNodeForBindings(template, captureContext, expressions);
  return dedupeBindingExpressions(expressions);
}

/**
 * Collects binding expressions for the default row template and every variant.
 */
export function collectLayoutListRowBindingExpressions(
  rowTemplate: ContainerNode,
  rowVariants: Array<{ name: string; template: ContainerNode }> | undefined,
  catalog?: SceneBehaviorCatalog,
): BindingExpression[] {
  const expressions: BindingExpression[] = [];
  expressions.push(
    ...collectRowTemplateBindingExpressions(rowTemplate, catalog),
  );
  for (const variant of rowVariants ?? []) {
    if (!variant.template) continue;
    expressions.push(
      ...collectRowTemplateBindingExpressions(variant.template, catalog),
    );
  }
  return dedupeBindingExpressions(expressions);
}

const bindingFingerprintValueEqual = (
  left: unknown,
  right: unknown,
): boolean => {
  if (Object.is(left, right)) return true;
  if (injectedValuesEqual(left, right)) return true;

  if (
    left != null &&
    right != null &&
    typeof left === "object" &&
    typeof right === "object"
  ) {
    const leftRecord = left as Record<string, unknown>;
    const rightRecord = right as Record<string, unknown>;
    if (
      "id" in leftRecord &&
      "id" in rightRecord &&
      leftRecord.id === rightRecord.id
    ) {
      return shallowEqualRecords(leftRecord, rightRecord);
    }
    return shallowEqualRecords(leftRecord, rightRecord);
  }

  return false;
};

/**
 * Returns whether two row binding fingerprints are equivalent for memoization.
 */
export function areRowBindingFingerprintsEqual(
  left: RowBindingFingerprint | undefined,
  right: RowBindingFingerprint | undefined,
): boolean {
  if (left === right) return true;
  if (!left || !right) return false;
  if (left.matchedVariantName !== right.matchedVariantName) return false;
  if (left.values.length !== right.values.length) return false;
  for (let index = 0; index < left.values.length; index += 1) {
    if (
      !bindingFingerprintValueEqual(left.values[index], right.values[index])
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Resolves every collected binding expression against row-scoped injected data.
 *
 * @param expressions - Binding AST collected from row templates.
 * @param scopedRowData - Injected data for a single list row.
 * @param variantConditionExpressions - Optional layout-list variant conditions.
 * @param resolveMatchedVariantName - Resolves which variant template applies.
 */
export function computeRowBindingFingerprint(
  expressions: readonly BindingExpression[],
  scopedRowData: InjectedData,
  variantConditionExpressions: readonly BindingExpression[],
  resolveMatchedVariantName?: () => string | null,
): RowBindingFingerprint {
  const values: unknown[] = [];
  for (const expression of expressions) {
    values.push(resolveBindingExpression(expression, scopedRowData));
  }
  for (const expression of variantConditionExpressions) {
    values.push(resolveBindingExpression(expression, scopedRowData));
  }

  return {
    values,
    matchedVariantName: resolveMatchedVariantName?.() ?? null,
  };
}

/**
 * Stable string key for layout-list row layout caches.
 */
export function serializeRowBindingFingerprint(
  fingerprint: RowBindingFingerprint,
): string {
  return stableSerialize({
    matchedVariantName: fingerprint.matchedVariantName,
    values: fingerprint.values.map((value) => {
      if (isDecimalLike(value)) return value.toString();
      return value;
    }),
  });
}

/**
 * Builds row-scoped injected data for a list preview row.
 */
export function buildLayoutListScopedRowData(
  layoutData: InjectedData,
  options: {
    rowIndex: number;
    rowItem: unknown;
    listSourceKey?: string;
    rowsListData?: unknown;
  },
): InjectedData {
  const scopedRowData: InjectedData = {
    ...layoutData,
    rowIndex: options.rowIndex,
    layoutListRowItem: options.rowItem,
  };
  if (options.listSourceKey) {
    (scopedRowData as unknown as Record<string, unknown>)[
      options.listSourceKey
    ] = options.rowsListData;
  }
  return scopedRowData;
}
